
<template>
  <div class="not-found-pages">
    <HeaderAdmin />
    <div class="dashboard-shell workspace-stack admin-shell">
      <section class="glass-surface workspace-hero workspace-hero--event admin-hero">
        <div>
          <span class="workspace-kicker">Admin FOMO</span>
          <h1 class="dashboard-title">Admin mas claro para pagos, ledger y reportes</h1>
          <p class="dashboard-subtitle">Ahora el admin separa mejor la operacion legacy, el ledger privado, los eventos editables y un reporte con rango de fechas y lectura mensual.</p>
        </div>
        <div class="admin-hero-stats">
          <article class="admin-stat"><small>Eventos</small><strong>{{ events.length }}</strong></article>
          <article class="admin-stat"><small>Ventas</small><strong>${{ money(heroSalesTotal) }}</strong></article>
          <article class="admin-stat"><small>FOMO bruto</small><strong>${{ money(heroFomoGross) }}</strong></article>
          <article class="admin-stat"><small>Pendiente org.</small><strong>${{ money(heroPendingTotal) }}</strong></article>
        </div>
      </section>

      <section class="admin-nav">
        <button v-for="item in sections" :key="item.key" :class="['admin-nav__card', { 'is-active': section === item.key }]" type="button" @click="section = item.key">
          <i :class="item.icon"></i>
          <div>
            <strong>{{ item.title }}</strong>
            <small>{{ item.copy }}</small>
          </div>
          <span>{{ item.meta }}</span>
        </button>
      </section>

      <section v-if="section === 'legacy'" class="workspace-stack">
        <article class="glass-surface workspace-panel">
          <div class="workspace-panel__header">
            <div>
              <span class="workspace-kicker">Legacy</span>
              <h2 class="workspace-title">Pagos heredados mejor agrupados</h2>
              <p class="workspace-copy">Lo viejo vive aqui en tres botones claros: aprobaciones, cobros manuales y revision de fees.</p>
            </div>
          </div>
          <div class="admin-subnav">
            <button :class="['workspace-tab', { 'is-active': legacySection === 'approvals' }]" type="button" @click="legacySection = 'approvals'">Aprobar pagos</button>
            <button :class="['workspace-tab', { 'is-active': legacySection === 'manuals' }]" type="button" @click="legacySection = 'manuals'">Cobros manuales</button>
            <button :class="['workspace-tab', { 'is-active': legacySection === 'fees' }]" type="button" @click="legacySection = 'fees'">Revision y fees</button>
          </div>
        </article>
        <AssistanceList v-show="legacySection === 'approvals'" v-if="!showLoading" style="padding-top: 20px; padding-bottom: 20px;" />
        <PendingPaymentList v-show="legacySection === 'manuals'" v-if="!showLoading" style="padding-top: 20px; padding-bottom: 20px;" />
        <PaymentFeeConfigList v-show="legacySection === 'fees'" v-if="!showLoading" style="padding-top: 20px; padding-bottom: 20px;" />
      </section>

      <section v-else-if="section === 'ledger'" class="workspace-stack">
        <article class="glass-surface workspace-panel">
          <div class="workspace-panel__header">
            <div>
              <span class="workspace-kicker">Ledger privado</span>
              <h2 class="workspace-title">Que entra, que gana FOMO, que cuesta banco y que falta</h2>
              <p class="workspace-copy">Filtra por fecha del evento y busca por nombre. El resumen ahora prioriza ventas organizer, ingreso FOMO, fee bancario, depositado y pendiente.</p>
            </div>
          </div>

          <div class="admin-filters">
            <label class="workspace-search admin-filters__search"><i class="fas fa-search"></i><input v-model="ledgerSearch" type="text" placeholder="Buscar evento o slug" /></label>
            <input v-model="ledgerDateFrom" class="workspace-input" type="date" />
            <input v-model="ledgerDateTo" class="workspace-input" type="date" />
            <select v-model="ledgerStatusFilter" class="workspace-input workspace-select"><option value="all">Todos</option><option value="reconciled">Reconciliados</option><option value="pending">Pendientes</option></select>
          </div>

          <div class="admin-kpis">
            <article v-for="card in ledgerCards" :key="card.label" class="admin-kpi"><small>{{ card.label }}</small><strong>${{ money(card.value) }}</strong><p>{{ card.copy }}</p></article>
          </div>

          <div v-if="loadingEvents" class="workspace-empty">Cargando ledger...</div>
          <div v-else-if="eventsError" class="workspace-feedback workspace-feedback--error">{{ eventsError }}</div>
          <div v-else-if="!ledgerEvents.length" class="workspace-empty">No hay eventos para ese filtro.</div>
          <div v-else class="workspace-table-shell">
            <table class="workspace-table">
              <thead><tr><th>Evento</th><th>Fecha</th><th>Ventas org.</th><th>Gana FOMO</th><th>Fee banco</th><th>Pendiente</th><th>Depositado</th><th>Estado</th><th></th></tr></thead>
              <tbody>
                <tr v-for="event in ledgerEvents" :key="event.id">
                  <td><div class="admin-name"><strong>{{ event.name }}</strong><small>{{ event.slug || 'sin-slug' }}</small></div></td>
                  <td>{{ formatDate(event.start_date) }}</td>
                  <td>${{ money(orgSales(event)) }}</td>
                  <td><div class="admin-name"><strong>${{ money(fomoRevenue(event)) }}</strong><small>Neto ${{ money(fomoNet(event)) }}</small></div></td>
                  <td>${{ money(bankFee(event)) }}</td>
                  <td>${{ money(orgPending(event)) }}</td>
                  <td>${{ money(orgDeposited(event)) }}</td>
                  <td><span :class="['workspace-status-pill', event.ledger_is_reconciled ? 'is-active' : 'is-warning']">{{ event.ledger_is_reconciled ? 'Reconciliado' : 'Por revisar' }}</span></td>
                  <td><button class="workspace-button workspace-button--primary workspace-button--small" type="button" @click="openEvent(event, 'finanzas')">Finanzas</button></td>
                </tr>
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <section v-else-if="section === 'events'" class="workspace-stack">
        <article class="glass-surface workspace-panel">
          <div class="workspace-panel__header">
            <div>
              <span class="workspace-kicker">Eventos</span>
              <h2 class="workspace-title">Directorio simple para editar y abrir checkout</h2>
              <p class="workspace-copy">Filtro por nombre, fecha del evento y estado temporal. Cada card abre edicion, finanzas o checkout.</p>
            </div>
          </div>

          <div class="admin-filters">
            <label class="workspace-search admin-filters__search"><i class="fas fa-search"></i><input v-model="eventSearch" type="text" placeholder="Buscar evento o descripcion" /></label>
            <input v-model="eventDateFrom" class="workspace-input" type="date" />
            <input v-model="eventDateTo" class="workspace-input" type="date" />
            <select v-model="eventPhase" class="workspace-input workspace-select"><option value="all">Todos</option><option value="next">Proximos</option><option value="prev">Anteriores</option></select>
          </div>

          <div class="admin-kpis admin-kpis--four">
            <article class="admin-kpi"><small>Eventos</small><strong>{{ filteredEvents.length }}</strong><p>Dentro del filtro actual.</p></article>
            <article class="admin-kpi"><small>Proximos</small><strong>{{ upcomingEvents }}</strong><p>Con fecha futura o activa.</p></article>
            <article class="admin-kpi"><small>Ventas</small><strong>${{ money(filteredEventSales) }}</strong><p>Venta acumulada filtrada.</p></article>
            <article class="admin-kpi"><small>FOMO bruto</small><strong>${{ money(filteredEventFomo) }}</strong><p>Fee plataforma mas Fomo Safe.</p></article>
          </div>

          <div v-if="loadingEvents" class="workspace-empty">Cargando eventos...</div>
          <div v-else-if="eventsError" class="workspace-feedback workspace-feedback--error">{{ eventsError }}</div>
          <div v-else-if="!filteredEvents.length" class="workspace-empty">No hay eventos para ese filtro.</div>
          <div v-else class="admin-grid">
            <article v-for="event in filteredEvents" :key="event.id" class="admin-card">
              <div class="workspace-badge-row workspace-badge-row--compact"><span class="workspace-badge">{{ bucket(event) === 'next' ? 'Proximo' : 'Anterior' }}</span><span class="workspace-badge">{{ formatDate(event.start_date) }}</span></div>
              <h3>{{ event.name }}</h3>
              <p>{{ event.description || 'Sin descripcion cargada.' }}</p>
              <div class="admin-meta">
                <span><i class="fas fa-link"></i>{{ event.slug || 'sin-slug' }}</span>
                <span><i class="fas fa-ticket-alt"></i>{{ event.total_ticket_count || 0 }} vendidos</span>
                <span><i class="fas fa-chart-line"></i>${{ money(event.total_ticket_sales) }}</span>
              </div>
              <div class="workspace-actions workspace-actions--compact workspace-actions--card">
                <button class="workspace-button workspace-button--primary workspace-button--small" type="button" @click="openEvent(event)">Editar</button>
                <button class="workspace-button workspace-button--small" type="button" @click="openEvent(event, 'finanzas')">Finanzas</button>
                <button class="workspace-button workspace-button--small" type="button" @click="openCheckout(event)">Checkout</button>
              </div>
            </article>
          </div>
        </article>
      </section>

      <section v-else class="workspace-stack">
        <article class="glass-surface workspace-panel">
          <div class="workspace-panel__header">
            <div>
              <span class="workspace-kicker">Reportes</span>
              <h2 class="workspace-title">Reporte personalizado por fechas</h2>
              <p class="workspace-copy">Este bloque usa fecha real de pago aprobado. Aqui ves ventas, fee banco, ingreso FOMO, neto FOMO y un grafico mensual del rango elegido.</p>
            </div>
          </div>

          <div class="admin-report-filters">
            <input v-model="reportDateFrom" class="workspace-input" type="date" />
            <input v-model="reportDateTo" class="workspace-input" type="date" />
            <div class="admin-presets">
              <button class="workspace-button workspace-button--small" type="button" @click="preset('30d')">30 dias</button>
              <button class="workspace-button workspace-button--small" type="button" @click="preset('90d')">90 dias</button>
              <button class="workspace-button workspace-button--small" type="button" @click="preset('ytd')">Ano actual</button>
              <button class="workspace-button workspace-button--small" type="button" @click="preset('all')">Todo</button>
            </div>
            <button class="workspace-button workspace-button--primary" type="button" :disabled="analyticsLoading" @click="refreshReport">{{ analyticsLoading ? 'Actualizando...' : 'Actualizar reporte' }}</button>
          </div>

          <div class="admin-kpis admin-kpis--four">
            <article v-for="card in reportCards" :key="card.label" class="admin-kpi"><small>{{ card.label }}</small><strong>{{ card.count ? card.value : `$${money(card.value)}` }}</strong><p>{{ card.copy }}</p></article>
          </div>

          <div v-if="analyticsError" class="workspace-feedback workspace-feedback--error">{{ analyticsError }}</div>
          <div v-else-if="analyticsLoading" class="workspace-empty">Cargando reporte...</div>
          <template v-else>
            <div class="admin-report-grid">
              <article class="admin-panel-card">
                <div class="workspace-panel__header"><div><span class="workspace-kicker">Grafico mensual</span><h3 class="workspace-title">Ventas vs neto FOMO</h3></div></div>
                <div v-if="!reportMonthly.length" class="workspace-empty">No hay pagos aprobados en ese rango.</div>
                <div v-else class="admin-chart">
                  <article v-for="bucket in reportMonthly" :key="bucket.month" class="admin-chart__item">
                    <div class="admin-chart__bars">
                      <span class="admin-chart__bar admin-chart__bar--sales" :style="{ height: chartHeight(bucket.gross_sales) }"></span>
                      <span class="admin-chart__bar admin-chart__bar--net" :style="{ height: chartHeight(bucket.fomo_net) }"></span>
                    </div>
                    <strong>{{ month(bucket.month) }}</strong>
                    <small>${{ money(bucket.gross_sales) }}</small>
                  </article>
                </div>
              </article>

              <article class="admin-panel-card">
                <div class="workspace-panel__header"><div><span class="workspace-kicker">Top eventos</span><h3 class="workspace-title">Mas movimiento en el rango</h3></div></div>
                <div v-if="!reportEvents.length" class="workspace-empty">Sin eventos con pagos aprobados.</div>
                <div v-else class="admin-top">
                  <article v-for="event in reportEvents.slice(0, 6)" :key="event.event_id" class="admin-top__item">
                    <div><strong>{{ event.name }}</strong><small>{{ formatDate(event.start_date) }}</small></div>
                    <div><strong>${{ money(event.gross_sales) }}</strong><small>FOMO neto ${{ money(event.fomo_net) }}</small></div>
                    <div class="workspace-actions workspace-actions--compact">
                      <button class="workspace-button workspace-button--primary workspace-button--small" type="button" @click="openEvent(event)">Resumen</button>
                      <button class="workspace-button workspace-button--small" type="button" @click="openEvent(event, 'finanzas')">Finanzas</button>
                    </div>
                  </article>
                </div>
              </article>
            </div>

            <div v-if="reportMonthly.length" class="workspace-table-shell">
              <table class="workspace-table">
                <thead><tr><th>Mes</th><th>Venta bruta</th><th>Ingreso FOMO</th><th>Fee banco</th><th>Neto FOMO</th><th>Ordenes</th></tr></thead>
                <tbody>
                  <tr v-for="bucket in reportMonthly" :key="bucket.month"><td>{{ month(bucket.month) }}</td><td>${{ money(bucket.gross_sales) }}</td><td>${{ money(bucket.fomo_revenue_gross) }}</td><td>${{ money(bucket.bank_fee_total) }}</td><td>${{ money(bucket.fomo_net) }}</td><td>{{ bucket.orders_count }}</td></tr>
                </tbody>
              </table>
            </div>
          </template>
        </article>
      </section>
    </div>
  </div>
</template>

<script>
import { apiService } from '../../common/api.services.js'
import AssistanceList from './AssistanceList.vue'
import PaymentFeeConfigList from './PaymentFeeConfigList.vue'
import PendingPaymentList from './PendingPaymentList.vue'
import HeaderAdmin from '../../components/HeaderAdmin.vue'

export default {
  name: 'AdminView',
  components: { HeaderAdmin, AssistanceList, PaymentFeeConfigList, PendingPaymentList },
  data() {
    const today = new Date()
    const yearStart = new Date(today.getFullYear(), 0, 1)
    return {
      section: 'legacy',
      legacySection: 'approvals',
      showLoading: false,
      events: [],
      loadingEvents: false,
      eventsError: '',
      ledgerSearch: '',
      ledgerDateFrom: '',
      ledgerDateTo: '',
      ledgerStatusFilter: 'all',
      eventSearch: '',
      eventDateFrom: '',
      eventDateTo: '',
      eventPhase: 'all',
      analyticsLoading: false,
      analyticsError: '',
      reportDateFrom: yearStart.toISOString().slice(0, 10),
      reportDateTo: today.toISOString().slice(0, 10),
      reportSummary: { gross_sales: 0, fomo_revenue_gross: 0, bank_fee_total: 0, fomo_net: 0, deposited_total: 0, orders_count: 0 },
      reportMonthly: [],
      reportEvents: [],
      reportLastUpdated: '',
    }
  },
  computed: {
    heroSalesTotal() { return this.events.reduce((sum, event) => sum + this.num(event.total_ticket_sales), 0) },
    heroFomoGross() { return this.events.reduce((sum, event) => sum + this.fomoRevenue(event), 0) },
    heroPendingTotal() { return this.events.reduce((sum, event) => sum + this.orgPending(event), 0) },
    sections() {
      return [
        { key: 'legacy', icon: 'fas fa-cash-register', title: 'Pagos legacy', copy: 'Aprobaciones, manuales y fees.', meta: '3 frentes' },
        { key: 'ledger', icon: 'fas fa-book', title: 'Ledger', copy: 'Contabilidad privada con numeros claros.', meta: `${this.ledgerEvents.length}` },
        { key: 'events', icon: 'fas fa-calendar-alt', title: 'Eventos', copy: 'Editar, abrir finanzas y checkout.', meta: `${this.filteredEvents.length}` },
        { key: 'reports', icon: 'fas fa-chart-line', title: 'Reportes', copy: 'Rango custom y grafico mensual.', meta: `${this.reportMonthly.length}` },
      ]
    },
    ledgerEvents() {
      const term = this.ledgerSearch.trim().toLowerCase()
      return this.events.filter((event) => {
        if (!this.inRange(event, this.ledgerDateFrom, this.ledgerDateTo)) return false
        if (this.ledgerStatusFilter === 'reconciled' && !event.ledger_is_reconciled) return false
        if (this.ledgerStatusFilter === 'pending' && event.ledger_is_reconciled) return false
        if (!term) return true
        return `${event.name || ''} ${event.slug || ''}`.toLowerCase().includes(term)
      }).slice().sort((a, b) => this.orgPending(b) - this.orgPending(a))
    },
    ledgerCards() {
      const orgSales = this.ledgerEvents.reduce((sum, event) => sum + this.orgSales(event), 0)
      const fomo = this.ledgerEvents.reduce((sum, event) => sum + this.fomoRevenue(event), 0)
      const bank = this.ledgerEvents.reduce((sum, event) => sum + this.bankFee(event), 0)
      const pending = this.ledgerEvents.reduce((sum, event) => sum + this.orgPending(event), 0)
      const deposited = this.ledgerEvents.reduce((sum, event) => sum + this.orgDeposited(event), 0)
      return [
        { label: 'Ventas organizer', value: orgSales, copy: 'Caja generada para organizer.' },
        { label: 'Ingreso bruto FOMO', value: fomo, copy: 'Fee plataforma mas Fomo Safe.' },
        { label: 'Fee banco', value: bank, copy: 'Costo del procesador.' },
        { label: 'Neto FOMO', value: fomo - bank, copy: 'Lo que queda luego del banco.' },
        { label: 'Pendiente', value: pending, copy: 'Lo que aun falta depositar.' },
        { label: 'Depositado', value: deposited, copy: 'Lo ya liquidado al organizer.' },
      ]
    },
    filteredEvents() {
      const term = this.eventSearch.trim().toLowerCase()
      return this.events.filter((event) => {
        if (!this.inRange(event, this.eventDateFrom, this.eventDateTo)) return false
        if (this.eventPhase !== 'all' && this.bucket(event) !== this.eventPhase) return false
        if (!term) return true
        return `${event.name || ''} ${event.slug || ''} ${event.description || ''}`.toLowerCase().includes(term)
      }).slice().sort((a, b) => (new Date(b.start_date || 0).getTime() || 0) - (new Date(a.start_date || 0).getTime() || 0))
    },
    upcomingEvents() { return this.filteredEvents.filter((event) => this.bucket(event) === 'next').length },
    filteredEventSales() { return this.filteredEvents.reduce((sum, event) => sum + this.num(event.total_ticket_sales), 0) },
    filteredEventFomo() { return this.filteredEvents.reduce((sum, event) => sum + this.fomoRevenue(event), 0) },
    reportCards() {
      return [
        { label: 'Venta bruta', value: this.reportSummary.gross_sales, copy: 'Pagos aprobados del rango.' },
        { label: 'Ingreso bruto FOMO', value: this.reportSummary.fomo_revenue_gross, copy: 'Fee plataforma mas Fomo Safe.' },
        { label: 'Fee banco', value: this.reportSummary.bank_fee_total, copy: 'Costo bancario del rango.' },
        { label: 'Neto FOMO', value: this.reportSummary.fomo_net, copy: 'Margen de FOMO del rango.' },
        { label: 'Depositos', value: this.reportSummary.deposited_total, copy: 'Settlements registrados.' },
        { label: 'Ordenes', value: this.reportSummary.orders_count, copy: 'Compras aprobadas.', count: true },
      ]
    },
    chartMax() {
      const values = this.reportMonthly.flatMap((item) => [this.num(item.gross_sales), this.num(item.fomo_net)])
      return values.length ? Math.max(...values, 1) : 1
    },
  },
  created() {
    this.syncTab()
    this.syncLegacy()
    this.loadEvents()
    this.loadReport()
  },
  methods: {
    num(value) { const parsed = Number.parseFloat(value); return Number.isNaN(parsed) ? 0 : parsed },
    money(value) { return this.num(value).toFixed(2) },
    dateKey(value) { return typeof value === 'string' ? value.slice(0, 10) : new Date(value).toISOString().slice(0, 10) },
    inRange(event, from, to) {
      const key = this.dateKey((event && (event.start_date || event.end_date)) || new Date())
      if (from && key < from) return false
      if (to && key > to) return false
      return true
    },
    bucket(event) {
      const date = new Date((event && (event.end_date || event.start_date)) || 0)
      return date.getTime() >= Date.now() ? 'next' : 'prev'
    },
    formatDate(value) { return value ? new Intl.DateTimeFormat('es-EC', { dateStyle: 'medium' }).format(new Date(value)) : 'Sin fecha' },
    month(value) { return value ? new Intl.DateTimeFormat('es-EC', { month: 'short', year: 'numeric' }).format(new Date(value)) : '--' },
    orgSales(event) { return this.num(event && event.ledger_organizer_sales_gross) },
    fomoRevenue(event) { return this.num(event && event.ledger_platform_fee_gross) + this.num(event && event.ledger_fomo_safe_gross) },
    bankFee(event) { return this.num(event && event.ledger_processor_fee_total) },
    fomoNet(event) { return this.fomoRevenue(event) - this.bankFee(event) },
    orgPending(event) { return this.num(event && event.ledger_organizer_pending) },
    orgDeposited(event) { return this.num(event && event.ledger_organizer_deposited) },
    syncTab() {
      const tab = typeof this.$route.query.tab === 'string' ? this.$route.query.tab : 'legacy'
      this.section = ['ledger', 'events', 'reports'].includes(tab) ? tab : 'legacy'
    },
    syncLegacy() {
      const legacy = typeof this.$route.query.legacy === 'string' ? this.$route.query.legacy : this.$route.query.tab
      this.legacySection = legacy === 'facturas' ? 'manuals' : legacy === 'fees' ? 'fees' : 'approvals'
    },
    syncRoute() {
      const query = { ...this.$route.query, tab: this.section === 'legacy' ? 'legacy' : this.section }
      if (this.section === 'legacy') query.legacy = this.legacySection === 'manuals' ? 'facturas' : this.legacySection === 'fees' ? 'fees' : 'transferencias'
      else delete query.legacy
      this.$router.replace({ name: 'AdminView', query })
    },
    loadEvents() {
      this.loadingEvents = true
      this.eventsError = ''
      Promise.all([apiService('/api/host/events/?show=next'), apiService('/api/host/events/?show=prev')]).then(([nextData, prevData]) => {
        if ((nextData && nextData.err) || (prevData && prevData.err)) {
          this.eventsError = (nextData && nextData.err) || (prevData && prevData.err) || 'No pudimos cargar los eventos.'
          this.events = []
          return
        }
        const items = [...(Array.isArray(nextData) ? nextData : (nextData && nextData.results) || []), ...(Array.isArray(prevData) ? prevData : (prevData && prevData.results) || [])]
        const map = new Map()
        items.forEach((event) => { if (event && event.id) map.set(event.id, event) })
        this.events = Array.from(map.values())
      }).finally(() => { this.loadingEvents = false })
    },
    loadReport() {
      this.analyticsLoading = true
      this.analyticsError = ''
      const query = new URLSearchParams()
      if (this.reportDateFrom) query.set('from', this.reportDateFrom)
      if (this.reportDateTo) query.set('to', this.reportDateTo)
      apiService(`/api/host/admin/finance-summary/${query.toString() ? `?${query.toString()}` : ''}`).then((data) => {
        if (data && data.err) {
          this.analyticsError = data.err
          this.reportSummary = { gross_sales: 0, fomo_revenue_gross: 0, bank_fee_total: 0, fomo_net: 0, deposited_total: 0, orders_count: 0 }
          this.reportMonthly = []
          this.reportEvents = []
          return
        }
        this.reportSummary = data && data.summary ? data.summary : this.reportSummary
        this.reportMonthly = data && Array.isArray(data.monthly) ? data.monthly : []
        this.reportEvents = data && Array.isArray(data.events) ? data.events : []
        this.reportLastUpdated = new Date().toISOString()
      }).finally(() => { this.analyticsLoading = false })
    },
    refreshReport() {
      if (this.reportDateFrom && this.reportDateTo && this.reportDateFrom > this.reportDateTo) {
        this.analyticsError = 'La fecha inicial no puede ser mayor a la final.'
        return
      }
      this.loadReport()
    },
    preset(type) {
      const today = new Date()
      const fmt = (value) => value.toISOString().slice(0, 10)
      if (type === 'all') {
        this.reportDateFrom = ''
        this.reportDateTo = fmt(today)
      } else if (type === 'ytd') {
        this.reportDateFrom = fmt(new Date(today.getFullYear(), 0, 1))
        this.reportDateTo = fmt(today)
      } else {
        const from = new Date(today)
        from.setDate(from.getDate() - (type === '90d' ? 90 : 30))
        this.reportDateFrom = fmt(from)
        this.reportDateTo = fmt(today)
      }
      this.loadReport()
    },
    chartHeight(value) {
      const amount = this.num(value)
      if (amount <= 0) return '6px'
      return `${Math.max(8, (amount / this.chartMax) * 100)}%`
    },
    openCheckout(event) {
      if (event && event.slug) window.open(`${window.location.origin}/event/${event.slug}`, '_blank')
    },
    openEvent(record, tab = 'resumen') {
      const id = record && (record.id || record.event_id)
      if (!id) return
      const query = tab !== 'resumen' ? { tab } : {}
      this.$router.push({ name: 'EventHostView', params: { slug: id }, query })
    },
  },
  watch: {
    '$route.query.tab'() { this.syncTab(); this.syncLegacy() },
    '$route.query.legacy'() { this.syncLegacy() },
    section() { this.syncRoute() },
    legacySection() { if (this.section === 'legacy') this.syncRoute() },
  },
}
</script>

<style>
.admin-shell { padding-bottom: 40px; }
.admin-hero { background: linear-gradient(135deg, rgba(10, 18, 40, 0.96), rgba(24, 40, 84, 0.94)); }
.admin-hero-stats, .admin-nav, .admin-kpis, .admin-grid, .admin-report-grid { display: grid; gap: 14px; }
.admin-hero-stats { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.admin-nav { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.admin-nav__card, .admin-stat, .admin-kpi, .admin-card, .admin-panel-card, .admin-top__item { padding: 16px; border-radius: 22px; background: rgba(255,255,255,0.04); border: 1px solid rgba(186,204,255,0.12); color: inherit; }
.admin-nav__card { display: grid; grid-template-columns: auto 1fr auto; gap: 12px; align-items: center; text-align: left; }
.admin-nav__card.is-active { background: linear-gradient(145deg, rgba(44,101,255,0.18), rgba(19,29,59,0.92)); border-color: rgba(121,169,255,0.42); }
.admin-nav__card small, .admin-stat small, .admin-kpi small, .admin-name small, .admin-meta span, .admin-top__item small { color: rgba(208,219,238,0.74); }
.admin-nav__card span:last-child { padding: 6px 10px; border-radius: 999px; background: rgba(255,255,255,0.08); }
.admin-subnav, .admin-presets { display: flex; flex-wrap: wrap; gap: 10px; }
.admin-filters, .admin-report-filters { display: grid; grid-template-columns: minmax(0,1.2fr) repeat(3, minmax(160px, 0.42fr)); gap: 12px; align-items: end; }
.admin-filters__search { width: 100%; }
.admin-kpis { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.admin-kpis--four { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.admin-kpi p, .admin-card p { margin: 0; color: rgba(224,232,246,0.82); }
.admin-name, .admin-meta, .admin-top { display: grid; gap: 6px; }
.admin-grid { grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); }
.admin-card h3 { margin: 0; }
.admin-meta span { display: inline-flex; align-items: center; gap: 8px; }
.admin-report-grid { grid-template-columns: minmax(0, 1.2fr) minmax(320px, 0.8fr); }
.admin-chart { display: grid; grid-template-columns: repeat(auto-fit, minmax(72px, 1fr)); gap: 10px; }
.admin-chart__item { display: grid; gap: 10px; text-align: center; }
.admin-chart__bars { height: 210px; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; align-items: end; padding: 10px; border-radius: 18px; background: rgba(255,255,255,0.03); border: 1px solid rgba(186,204,255,0.08); }
.admin-chart__bar { width: 100%; min-height: 6px; border-radius: 14px 14px 6px 6px; }
.admin-chart__bar--sales { background: linear-gradient(180deg, #8dd0ff, #3f83f8); }
.admin-chart__bar--net { background: linear-gradient(180deg, #7bf1c8, #16a34a); }
.admin-top__item { display: grid; gap: 10px; }
@media (max-width: 1180px) { .admin-hero-stats, .admin-nav, .admin-kpis--four, .admin-report-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 920px) { .admin-hero-stats, .admin-nav, .admin-kpis, .admin-kpis--four, .admin-filters, .admin-report-filters, .admin-report-grid { grid-template-columns: 1fr; } .admin-nav__card { grid-template-columns: auto 1fr; } .admin-nav__card span:last-child { grid-column: 1 / -1; justify-self: start; } }
</style>
